import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'
import { BusReservation, TeamType } from '../../types'

interface SupabaseBusStore {
  reservations: BusReservation[]
  loading: boolean
  error: string | null
  
  // Actions
  fetchReservations: () => Promise<void>
  addReservation: (reservation: Omit<BusReservation, 'id' | 'status'>) => Promise<{ error?: string }>
  updateReservationStatus: (id: string, status: 'pending' | 'approved' | 'rejected') => Promise<{ error?: string }>
  deleteReservation: (id: string) => Promise<{ error?: string }>
}

export const useSupabaseBusStore = create<SupabaseBusStore>((set, get) => ({
  reservations: [],
  loading: false,
  error: null,

  fetchReservations: async () => {
    console.log('🚌 Bus Store - fetchReservations started')
    set({ loading: true, error: null })
    try {
      const { profile, user } = useAuthStore.getState()
      console.log('🚌 Bus Store - Current profile:', profile, 'user:', user)
      
      if (!profile && !user) {
        throw new Error('Nicht angemeldet - weder Profile noch User gefunden')
      }

      // Use profile if available, otherwise fall back to user
      const currentUser = profile || user
      if (!currentUser) {
        throw new Error('Keine Benutzerdaten verfügbar')
      }

      console.log('🚌 Bus Store - Using user data:', currentUser)

      let query = supabase.from('bus_reservations').select('*')
      
      // Check if user has admin privileges or if filtering is needed
      if (currentUser.role === 'trainer') {
        console.log('🚌 Bus Store - Filtering for trainer:', currentUser.id)
        query = query.eq('user_id', currentUser.id)
      } else {
        console.log('🚌 Bus Store - Admin/unrestricted view - showing all reservations')
      }
      
      console.log('🚌 Bus Store - Executing query...')
      const { data, error } = await query.order('created_at', { ascending: false })
      console.log('🚌 Bus Store - Query result:', { data, error, dataLength: data?.length })

      if (error) {
        console.error('🚌 Bus Store - Supabase error:', error)
        throw error
      }

      // Daten in das erwartete Format konvertieren
      const reservations = (data || []).map(item => ({
        id: item.id,
        date: item.date,
        startTime: item.start_time,
        endTime: item.end_time || '',
        destination: item.destination,
        purpose: item.purpose,
        driver: item.driver,
        contactPhone: item.contact_phone,
        team: item.team as TeamType,
        status: item.status,
        notes: item.notes ?? undefined,
      }))

      console.log('🚌 Bus Store - Processed reservations:', reservations)
      set({ reservations, loading: false, error: null })
    } catch (error: any) {
      console.error('❌ Bus Store - fetchReservations error:', error)
      const errorMessage = error.message || 'Unbekannter Fehler beim Laden der Reservierungen'
      set({ error: errorMessage, loading: false })
    }
  },

  addReservation: async (reservation) => {
    console.log('🚌 Bus Store - addReservation started with:', reservation)
    try {
      const { user } = useAuthStore.getState()
      console.log('🚌 Bus Store - Current user:', user)
      if (!user) return { error: 'Nicht angemeldet' }

      const insertData = {
        date: reservation.date,
        start_time: reservation.startTime,
        end_time: reservation.endTime || null,
        destination: reservation.destination,
        purpose: reservation.purpose,
        driver: reservation.driver,
        contact_phone: reservation.contactPhone,
        team: reservation.team,
        notes: reservation.notes,
        user_id: user.id,
      }
      
      console.log('🚌 Bus Store - Insert data:', insertData)

      const { error } = await supabase
        .from('bus_reservations')
        .insert(insertData)

      if (error) {
        console.error('❌ Bus Store - Insert error:', error)
        throw error
      }

      console.log('✅ Bus Store - Reservation added successfully')
      
      // Reservierungen neu laden
      get().fetchReservations()
      return {}
    } catch (error: any) {
      console.error('❌ Bus Store - addReservation error:', error)
      return { error: error.message }
    }
  },

  updateReservationStatus: async (id, status) => {
    try {
      const { error } = await supabase
        .from('bus_reservations')
        .update({ status })
        .eq('id', id)

      if (error) throw error

      // Reservierungen neu laden
      get().fetchReservations()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  deleteReservation: async (id) => {
    try {
      const { error } = await supabase
        .from('bus_reservations')
        .delete()
        .eq('id', id)

      if (error) throw error

      // Reservierungen neu laden
      get().fetchReservations()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },
}))
